import type { Teammate } from "@repo/app-types/entities/teammate";
import { useId, type FC, type ReactNode } from "react";
import { twMerge } from "tailwind-merge";

import { TeammateAvatar } from "../../teammate/teammateAvatar.js";

export interface AwardCardData {
  readonly title: string;
  readonly recipient?: string;
  readonly teammate?: Teammate;
  readonly detail?: ReactNode;
  readonly accent?: "gold" | "green" | "blue" | "amber" | "gray";
}

interface AwardCardProps extends AwardCardData {
  readonly header?: string;
  readonly className?: string;
  readonly isLoading?: boolean;
}

const ACCENT_GRADIENTS: Record<NonNullable<AwardCardData["accent"]>, string> = {
  gold: "from-amber-300 via-amber-700 to-amber-300",
  green: "from-emerald-300 via-emerald-600 to-emerald-300",
  blue: "from-sky-300 via-sky-600 to-sky-300",
  amber: "from-orange-300 via-orange-600 to-orange-300",
  gray: "from-gray-300 via-gray-500 to-gray-300",
};

const ACCENT_LOGO_COLORS: Record<
  NonNullable<AwardCardData["accent"]>,
  { logo: string; text: string; shadow: string }
> = {
  gold: {
    logo: "text-amber-500",
    text: "text-amber-700 drop-shadow-[0_1px_0_rgba(255,243,200,0.7)]",
    shadow: "drop-shadow-[0_2px_4px_rgba(152,105,28,0.35)]",
  },
  green: {
    logo: "text-emerald-500",
    text: "text-emerald-700 drop-shadow-[0_1px_0_rgba(209,250,229,0.7)]",
    shadow: "drop-shadow-[0_2px_4px_rgba(5,150,105,0.35)]",
  },
  blue: {
    logo: "text-sky-500",
    text: "text-sky-700 drop-shadow-[0_1px_0_rgba(186,230,253,0.7)]",
    shadow: "drop-shadow-[0_2px_4px_rgba(2,132,199,0.35)]",
  },
  amber: {
    logo: "text-orange-500",
    text: "text-orange-700 drop-shadow-[0_1px_0_rgba(255,237,213,0.7)]",
    shadow: "drop-shadow-[0_2px_4px_rgba(194,65,12,0.35)]",
  },
  gray: {
    logo: "text-muted-foreground",
    text: "text-gray-600 drop-shadow-[0_1px_0_rgba(243,244,246,0.7)]",
    shadow: "drop-shadow-[0_2px_4px_rgba(107,114,128,0.35)]",
  },
};

const AlpharunLogo: FC<{
  className?: string;
  title?: string;
  shimmer?: boolean;
}> = ({ className, title = "Alpharun", shimmer = false }) => {
  const gradientId = useId();
  const goldGradientId = `alpharun-gold-${gradientId}`;

  return (
    <svg
      className={className}
      width="324"
      height="163"
      viewBox="0 0 324 163"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={title}
    >
      <title>{title}</title>
      {shimmer ? (
        <defs>
          <linearGradient
            id={goldGradientId}
            x1="-324"
            y1="0"
            x2="0"
            y2="0"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor="#9A6A1B" />
            <stop offset="35%" stopColor="#F2C94C" />
            <stop offset="50%" stopColor="#FFF1A6" />
            <stop offset="65%" stopColor="#F2C94C" />
            <stop offset="100%" stopColor="#9A6A1B" />
            <animate
              attributeName="x1"
              from="-324"
              to="324"
              dur="2.6s"
              repeatCount="indefinite"
            />
            <animate
              attributeName="x2"
              from="0"
              to="648"
              dur="2.6s"
              repeatCount="indefinite"
            />
          </linearGradient>
        </defs>
      ) : null}
      <path
        d="M232.134 0.606793C232.36 0.230332 232.766 0 233.205 0H321.004C321.976 0 322.576 1.06004 322.076 1.8932L225.963 162.054C225.737 162.431 225.331 162.661 224.892 162.661H137.093C136.121 162.661 135.521 161.601 136.021 160.768L232.134 0.606793Z"
        fill={shimmer ? `url(#${goldGradientId})` : "currentColor"}
      />
      <path
        d="M187.835 0.606793C188.061 0.230332 188.468 0 188.907 0H208.705C209.677 0 210.277 1.06005 209.777 1.8932L113.665 162.054C113.439 162.431 113.032 162.661 112.593 162.661H92.7939C91.8222 162.661 91.2221 161.601 91.722 160.768L187.835 0.606793Z"
        fill={shimmer ? `url(#${goldGradientId})` : "currentColor"}
      />
      <path
        d="M142.542 0.606793C142.768 0.230332 143.175 0 143.614 0H163.413C164.384 0 164.984 1.06005 164.484 1.8932L68.3716 162.054C68.1457 162.431 67.7388 162.661 67.2998 162.661H47.5009C46.5292 162.661 45.9291 161.601 46.4291 160.768L142.542 0.606793Z"
        fill={shimmer ? `url(#${goldGradientId})` : "currentColor"}
      />
      <path
        d="M97.2489 0.606793C97.4748 0.230332 97.8816 0 98.3207 0H118.12C119.091 0 119.691 1.06005 119.191 1.8932L23.0786 162.054C22.8527 162.431 22.4458 162.661 22.0068 162.661H2.20793C1.23627 162.661 0.636134 161.601 1.13611 160.768L97.2489 0.606793Z"
        fill={shimmer ? `url(#${goldGradientId})` : "currentColor"}
      />
    </svg>
  );
};

export const AwardCard: FC<AwardCardProps> = ({
  header,
  title,
  recipient,
  teammate,
  detail,
  isLoading,
  className,
  accent = "gold",
}) => {
  const headerLabel = header ?? title;
  const shouldShowSubheader = header !== undefined && header !== title;
  const gradient = ACCENT_GRADIENTS[accent];
  const colors = ACCENT_LOGO_COLORS[accent];

  return (
    <div
      className={twMerge(
        "shimmer relative rounded-md bg-gradient-to-r p-3",
        gradient,
      )}
    >
      <div
        className={twMerge(
          className,
          "relative flex flex-col gap-6 px-7 pt-6 pb-7 ring-0",
        )}
      >
        <div className="flex items-center gap-4">
          <AlpharunLogo
            className={twMerge(
              "h-10 w-10 shrink-0",
              colors.logo,
              colors.shadow,
            )}
            shimmer
          />
          <span
            className={twMerge(
              "text-base font-semibold tracking-[0.22em]",
              colors.text,
            )}
          >
            {headerLabel}
          </span>
        </div>
        <div className="flex flex-col gap-4">
          {isLoading ? (
            <div className="text-sm text-gray-400">Loading...</div>
          ) : (
            <>
              {shouldShowSubheader ? (
                <div className="truncate text-xs font-semibold text-muted-foreground">
                  {title}
                </div>
              ) : null}
              {recipient ? (
                <div className="flex items-center gap-3">
                  {teammate ? <TeammateAvatar teammate={teammate} /> : null}
                  <div className="truncate text-lg font-semibold text-foreground">
                    {recipient}
                  </div>
                </div>
              ) : null}
              {detail ? (
                <div>
                  {typeof detail === "string" ? (
                    <p className="text-xs text-muted-foreground">{detail}</p>
                  ) : (
                    detail
                  )}
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
